import { c as _c } from "react/compiler-runtime";
import { useTimezone } from "@/hooks/use-timezone";
import { CALENDAR_TITLE_WRAP_CLASSNAME, segmentHeaderClassName, useCalendarTextSize } from "@/lib/calendar-text-size";
import { cx } from "@/lib/tailwind-utils";
import { EventCleanupSegmentTiny } from "@/types/event";
import { SEGMENT_TITLE_STYLE } from "./calendar-event/appointment-service-segment-card";
import { CalendarTimeRange } from "./calendar-event/calendar-time-range";

/** Default color for cleanup time segments when no color is specified */
export const CLEANUP_TIME_DEFAULT_COLOR = "hsl(var(--foreground-tertiary))";
interface CleanupTimeSliceProps {
  cleanupTime: EventCleanupSegmentTiny;
  /** Start percentage (0-100) from top of container */
  topPct: number;
  /** End percentage (0-100) from bottom of container */
  bottomPct: number;
  /** Additional styles to apply */
  style?: React.CSSProperties;
  /** Optional class name override */
  className?: string;
  /** Patient name to display after cleanup label */
  patientName?: string;
}
export function CleanupTimeSlice(t0) {
  const $ = _c(54);
  const {
    cleanupTime,
    topPct,
    bottomPct,
    style,
    className,
    patientName
  } = t0;
  const timeZone = useTimezone();
  const textSize = useCalendarTextSize();
  let cleanupEnd;
  let cleanupStart;
  let customColor;
  let t1;
  let t2;
  if ($[0] !== cleanupTime.color || $[1] !== cleanupTime.end || $[2] !== cleanupTime.start) {
    cleanupStart = new Date(cleanupTime.start);
    if ($[8] !== cleanupTime.color || $[9] !== cleanupTime.end) {
      cleanupEnd = new Date(cleanupTime.end);
      customColor = cleanupTime.color;
      t1 = cleanupEnd.getTime();
      $[8] = cleanupTime.color;
      $[9] = cleanupTime.end;
      $[10] = cleanupEnd;
      $[11] = customColor;
      $[12] = t1;
    } else {
      cleanupEnd = $[10];
      customColor = $[11];
      t1 = $[12];
    }
    t2 = cleanupStart.getTime();
    $[0] = cleanupTime.color;
    $[1] = cleanupTime.end;
    $[2] = cleanupTime.start;
    $[3] = cleanupEnd;
    $[4] = cleanupStart;
    $[5] = customColor;
    $[6] = t1;
    $[7] = t2;
  } else {
    cleanupEnd = $[3];
    cleanupStart = $[4];
    customColor = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  const cleanupDurationMinutes = (t1 - t2) / 60000;
  const t3 = cleanupDurationMinutes <= 15 ? "overflow-hidden whitespace-nowrap" : cleanupDurationMinutes <= 20 ? "line-clamp-2 break-words" : "break-words";
  const t4 = !customColor && "border-foreground-tertiary/25 bg-foreground-tertiary/20";
  let t5;
  if ($[13] !== className || $[14] !== t3 || $[15] !== t4) {
    t5 = cx("pointer-events-auto absolute left-0 right-0 overflow-hidden rounded-md [container-type:size] border", t3, t4, className);
    $[13] = className;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = `${topPct}%`;
  const t7 = `${bottomPct}%`;
  let t8;
  if ($[17] !== customColor) {
    t8 = customColor && {
      backgroundColor: `${customColor}50`,
      borderColor: `${customColor}40`
    };
    $[17] = customColor;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== style || $[20] !== t6 || $[21] !== t7 || $[22] !== t8) {
    t9 = {
      top: t6,
      bottom: t7,
      ...t8,
      ...style
    };
    $[19] = style;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const t10 = !customColor && "border-l-foreground-tertiary";
  let t11;
  if ($[24] !== t10) {
    t11 = cx("flex h-full rounded-l-md border-l-[3px]", t10);
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== customColor) {
    t12 = customColor ? {
      borderLeftColor: customColor
    } : undefined;
    $[26] = customColor;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== textSize) {
    t13 = segmentHeaderClassName(textSize);
    $[28] = textSize;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = cx("overflow-hidden text-calendar-tiny font-medium leading-tight", CALENDAR_TITLE_WRAP_CLASSNAME);
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const t15 = cleanupTime.title ?? "Cleanup";
  let t16;
  if ($[31] !== t15) {
    t16 = <span>{t15}</span>;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = patientName && ` - ${patientName}`;
  let t18;
  if ($[33] !== t17) {
    t18 = <span>{t17}</span>;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t16 || $[36] !== t18) {
    t19 = <div className={t14} style={SEGMENT_TITLE_STYLE}>{t16}{t18}</div>;
    $[35] = t16;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== cleanupEnd || $[39] !== cleanupStart || $[40] !== timeZone) {
    t20 = <div className="max-w-full truncate text-calendar-micro opacity-80"><CalendarTimeRange start={cleanupStart} end={cleanupEnd} timeZone={timeZone} /></div>;
    $[38] = cleanupEnd;
    $[39] = cleanupStart;
    $[40] = timeZone;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t13 || $[43] !== t19 || $[44] !== t20) {
    t21 = <div className="ml-1 min-w-0 flex-1"><div className={t13}>{t19}{t20}</div></div>;
    $[42] = t13;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t11 || $[47] !== t12 || $[48] !== t21) {
    t22 = <div className={t11} style={t12}>{t21}</div>;
    $[46] = t11;
    $[47] = t12;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t22 || $[51] !== t5 || $[52] !== t9) {
    t23 = <div className={t5} style={t9}>{t22}</div>;
    $[50] = t22;
    $[51] = t5;
    $[52] = t9;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  return t23;
}